import {
  DashboardCard,
  DashboardGrid,
  PageHeader,
  SectionHeader,
} from '@zafroi/ui-kit';
import { ArrowUpRight, Building2, KeyRound, ShieldCheck, Users } from 'lucide-react';
import { useTenant } from '@/services/tenant-context';

/**
 * Engage Settings — read-only window into workspace identity.
 *
 * Engage deliberately does NOT host edit UI for tenant identity, team,
 * security, or API keys. Those concerns live in the Operations app today
 * (single source of truth via /auth/me from the runtime). When the
 * cross-app platform-app lands (docs/BACKLOG.md → "Platform-app for
 * shared workspace settings"), the "Open in Operations →" links here
 * point to platform.zafroi.com instead.
 */
export function SettingsPage() {
  const { currentTenant } = useTenant();

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        description="Workspace-wide preferences."
      />

      <DashboardGrid columns={2}>
        <DashboardCard
          title="Workspace"
          description="Tenant identity — managed in Operations."
          icon={Building2}
        >
          <dl className="grid grid-cols-[120px_1fr] gap-y-2 text-sm">
            <dt className="text-muted-foreground">Display name</dt>
            <dd className="font-medium text-foreground">{currentTenant.name}</dd>
            <dt className="text-muted-foreground">Short code</dt>
            <dd className="font-mono text-[12.5px] text-foreground">
              {currentTenant.code ?? '—'}
            </dd>
          </dl>
          <ManageInPlatformLink path="/platform/organizations" />
        </DashboardCard>

        <DashboardCard
          title="Team"
          description="Members, roles, and assignment routing."
          icon={Users}
        >
          <p className="text-sm text-muted-foreground">
            Team configuration is centralised in the platform console.
          </p>
          <ManageInPlatformLink path="/users" />
        </DashboardCard>

        <DashboardCard
          title="Security"
          description="Session, MFA, and access controls."
          icon={ShieldCheck}
        >
          <p className="text-sm text-muted-foreground">
            Security policies inherit from the platform. Tenant-specific
            overrides are managed in the platform console.
          </p>
          <ManageInPlatformLink path="/users" />
        </DashboardCard>

        <DashboardCard
          title="API keys"
          description="Programmatic access to your engagement data."
          icon={KeyRound}
        >
          <p className="text-sm text-muted-foreground">
            API clients + keys are issued from the platform console so the same
            credential surface backs every ZAFROI app.
          </p>
          <ManageInPlatformLink path="/platform/api-keys" />
        </DashboardCard>
      </DashboardGrid>

      <SectionHeader
        withDivider
        title="Danger zone"
        description="Irreversible actions for this workspace."
      />
      <DashboardCard>
        <p className="text-sm text-muted-foreground">
          Destructive workspace actions (delete, transfer ownership, etc.)
          live in Operations alongside the rest of tenant administration.
        </p>
      </DashboardCard>
    </div>
  );
}

/**
 * Cross-app link to the matching settings surface in Operations. Today
 * Operations is at zafroi.com; when the platform-app lands these point
 * at platform.zafroi.com instead.
 */
const PLATFORM_APP_URL =
  ((import.meta.env.VITE_PLATFORM_APP_URL as string | undefined) ?? '')
    .trim()
    .replace(/\/+$/, '') || 'https://platform.zafroi.com';

// Account/identity administration (org, members, security, API keys) lives in
// the platform control-plane app (ADR-014), not per-product. Link out there.
function ManageInPlatformLink({ path }: { path: string }) {
  return (
    <a
      href={`${PLATFORM_APP_URL}${path}`}
      className="mt-4 inline-flex items-center gap-1 text-[12.5px] font-medium text-primary hover:underline"
    >
      Open in Platform
      <ArrowUpRight className="h-3 w-3" />
    </a>
  );
}
